import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router';

import { focusElement } from 'platform/utilities/ui';
import { setData } from 'platform/forms-system/src/js/actions';
import { dismissITFMessage as dismissITFMessageAction } from '../actions';
import { initSessionTracking } from '../../shared/utils/tracking/datadogRumTracking';
import { trackFormResumption } from '../utils/tracking/features/allClaimsTracking';
import { initBddShaTracking } from '../utils/tracking/bddShaTracking';
import { hasScreenerAnswers } from '../subtask/utils';
import {
  TRACKING_526EZ_SIDENAV_BACK_BUTTON_CLICKS,
  TRACKING_526EZ_SIDENAV_CONTINUE_BUTTON_CLICKS,
  TRACKING_526EZ_SIDENAV_CLICKS,
  DISABILITY_526_V2_ROOT_URL,
  GET_STARTED_PATH,
} from '../constants';

import {
  itfMessage,
  itfError,
  itfSuccess,
  itfActive,
} from '../content/itfWrapper';

export class ITFBanner extends React.Component {
  componentDidUpdate(prevProps) {
    // Track form resumption when ITF banner is dismissed
    if (!prevProps.messageDismissed && this.props.messageDismissed) {
      // Clear old session click counts before tracking resumption
      try {
        sessionStorage.removeItem(TRACKING_526EZ_SIDENAV_BACK_BUTTON_CLICKS);
        sessionStorage.removeItem(
          TRACKING_526EZ_SIDENAV_CONTINUE_BUTTON_CLICKS,
        );
        sessionStorage.removeItem(TRACKING_526EZ_SIDENAV_CLICKS);
      } catch (error) {
        // Storage access blocked - silent fail
      }
      trackFormResumption(this.props.formData);
      initSessionTracking(this.props.formData, this.props.setFormData);
      initBddShaTracking(this.props.formData);
    }
  }

  dismissMessage = () => {
    this.props.dismissITFMessage();
  };

  render() {
    if (this.props.messageDismissed) {
      return this.props.children;
    }

    let message;
    switch (this.props.status) {
      case 'error':
        message = itfMessage(
          'We can’t confirm if we have an intent to file on record for you right now',
          itfError,
          'info',
        );
        break;
      case 'itf-found':
        message = itfMessage(
          'You already have an Intent to File',
          itfActive(this.props.currentExpDate),
          'success',
        );
        break;
      case 'itf-created': {
        const { previousITF, currentExpDate, previousExpDate } = this.props;
        message = itfMessage(
          'Your Intent to File request has been submitted',
          itfSuccess(previousITF, currentExpDate, previousExpDate),
          'success',
        );
        break;
      }
      default:
        throw new Error(
          `Unexpected status prop in ITFBanner: ${this.props.status}`,
        );
    }

    setTimeout(() => {
      focusElement('.itf-wrapper');
    });

    // Veterans who came through the screener go back to it
    const backToScreener = hasScreenerAnswers();

    return (
      <div className="vads-l-grid-container vads-u-padding-left--0 vads-u-padding-bottom--5">
        <div className="usa-content">
          <h1>{this.props.title}</h1>
          {message}
          <Link
            className="vads-u-margin-right--2"
            to={backToScreener ? GET_STARTED_PATH : DISABILITY_526_V2_ROOT_URL}
          >
            Back
          </Link>
          <button
            type="button"
            className="usa-button-primary"
            onClick={this.dismissMessage}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }
}

ITFBanner.propTypes = {
  status: PropTypes.oneOf(['error', 'itf-found', 'itf-created']).isRequired,
  children: PropTypes.node,
  currentExpDate: PropTypes.string,
  dismissITFMessage: PropTypes.func,
  formData: PropTypes.object,
  messageDismissed: PropTypes.bool,
  previousExpDate: PropTypes.string,
  previousITF: PropTypes.object,
  setFormData: PropTypes.func,
  title: PropTypes.string,
};

const mapStateToProps = state => ({
  formData: state.form.data,
  messageDismissed: state.itf.messageDismissed,
});

const mapDispatchToProps = {
  dismissITFMessage: dismissITFMessageAction,
  setFormData: setData,
};

export default connect(mapStateToProps, mapDispatchToProps)(ITFBanner);
